<template>
  <div class="lang-pills">
    <!-- A pill is a plain wrapper (not a <button>) so the inner remove control is
         an independent, reliably-clickable element. -->
    <div
        v-for="lang in active"
        :key="lang"
        class="lang-pill"
        :class="{ 'lang-pill--active': lang === value }"
        @click="$emit('input', lang)"
    >
      <!-- Default-language star: filled on the current default, hollow on the rest.
           Clicking a hollow star makes that language the profile default. -->
      <span
          v-if="showDefault"
          class="lang-pill__star"
          :class="{ 'lang-pill__star--on': lang === defaultLang }"
          role="button"
          :title="lang === defaultLang ? (defaultLabel || 'Default language') : (setDefaultLabel || 'Set as default language')"
          @click.stop="$emit('set-default', lang)"
      >{{ lang === defaultLang ? '★' : '☆' }}</span>
      <span class="lang-pill__code">{{ codeLabel(lang) }}</span>
      <!-- Non-default languages can be removed; defaults (de/en) and the chosen
           default language cannot. -->
      <span
          v-if="removable && !defaults.includes(lang) && lang !== defaultLang"
          class="lang-pill__remove"
          role="button"
          :title="(removeLabel || 'Remove')"
          @click.stop="$emit('remove', lang)"
      >&times;</span>
    </div>

    <!-- '+' add control: only while there are languages left to add. -->
    <md-menu v-if="remaining.length" md-direction="bottom-start" md-size="small">
      <md-button class="lang-pill lang-pill--add md-icon-button" md-menu-trigger :title="addLabel">
        <md-icon>add</md-icon>
      </md-button>
      <md-menu-content>
        <md-menu-item v-for="lang in remaining" :key="lang" @click="$emit('add', lang)">
          {{ codeLabel(lang) }}
        </md-menu-item>
      </md-menu-content>
    </md-menu>
  </div>
</template>

<script>
/**
 * Brand-style language switcher: pills for the currently-active languages plus a
 * '+' menu to add any not-yet-added language. The '+' disappears once every
 * supported language is active. Default languages can't be removed.
 *
 * Props:
 *  - all:       full list of supported lang codes, e.g. ['de','en','fr','nl','el']
 *  - active:    currently-added lang codes (order preserved), e.g. ['de','en']
 *  - value:     selected lang code (v-model)
 *  - defaults:  codes that are always present and non-removable (default ['de','en'])
 *  - removable: whether non-default pills show a remove (×) control
 *  - langLabels: optional map { de: 'Deutsch', ... } for nicer labels
 *  - showDefault: whether to show the default-language star control
 *  - defaultLang: the currently-default language code (the filled ★)
 *
 * Emits: input (select), add (lang), remove (lang), set-default (lang)
 */
export default {
  name: 'LanguagePills',
  props: {
    all: { type: Array, required: true },
    active: { type: Array, required: true },
    value: { type: String, default: null },
    defaults: { type: Array, default: () => ['de', 'en'] },
    removable: { type: Boolean, default: true },
    langLabels: { type: Object, default: () => ({}) },
    addLabel: { type: String, default: 'Add language' },
    removeLabel: { type: String, default: 'Remove' },
    showDefault: { type: Boolean, default: false },
    defaultLang: { type: String, default: null },
    defaultLabel: { type: String, default: 'Default language' },
    setDefaultLabel: { type: String, default: 'Set as default language' }
  },
  computed: {
    remaining() {
      return this.all.filter(l => !this.active.includes(l));
    }
  },
  methods: {
    codeLabel(lang) {
      return this.langLabels[lang] || lang.toUpperCase();
    }
  }
};
</script>

<style scoped>
.lang-pills {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #eef0f3;
  border-radius: 999px;
  padding: 5px;
}

.lang-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  min-width: 48px;
  height: 34px;
  box-sizing: border-box;
  border: none;
  background: transparent;
  color: #5b626b;
  font-weight: 700;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  padding: 0 0.95rem;
  border-radius: 999px;
  cursor: pointer;
  line-height: 1;
  transition: background .15s ease, color .15s ease, box-shadow .15s ease;
}
.lang-pill__code { line-height: 1; }

/* Default-language star: hollow by default, gold + filled on the active default. */
.lang-pill__star {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  line-height: 1;
  margin-left: -0.25rem;
  color: inherit;
  opacity: 0.5;
  cursor: pointer;
  transition: opacity .15s ease, color .15s ease, transform .1s ease;
}
.lang-pill__star:hover { opacity: 1; transform: scale(1.15); }
.lang-pill__star--on { color: #f2b705; opacity: 1; }
.lang-pill--active .lang-pill__star--on { color: #ffd34d; }
.lang-pill:hover { color: #2c2c2c; }
.lang-pill--active {
  background: #343841;
  color: #fff;
  box-shadow: 0 2px 8px rgba(52, 56, 65, 0.25);
}
.lang-pill--active:hover { color: #fff; }

.lang-pill__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 14px;
  line-height: 1;
  color: inherit;
  opacity: 0.65;
  margin-right: -0.3rem;
  cursor: pointer;
}
.lang-pill__remove:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.2);
}

/* '+' button: pill-sized and centered, not the default md-icon-button circle */
.lang-pill--add {
  min-width: 38px;
  width: auto;
  height: 34px;
  margin: 0;
  padding: 0 0.7rem;
  color: #5b626b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.lang-pill--add:hover { color: #2c2c2c; }
.lang-pill--add .md-icon { font-size: 19px !important; color: inherit !important; margin: 0 !important; }
</style>
